import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../../utils/api/axiosInstance";
import { useUserAuth } from "../../contexts/UserAuthContext";
import PublicPageLink from "../common/PublicPageLink";

/**
 * User-facing controls for match personalization — no engine internals.
 */
const MatchPersonalizationSection = ({ showToast }) => {
  const { user, setUser } = useUserAuth();
  const [resetting, setResetting] = useState(false);
  const [savingStyle, setSavingStyle] = useState(false);
  const [showShoppingStyle, setShowShoppingStyle] = useState(
    user?.profile?.showShoppingStyle === true,
  );
  const [shoppingStyleLabel, setShoppingStyleLabel] = useState(
    user?.profile?.shoppingStyleLabel || "",
  );

  const handleReset = async () => {
    if (
      !window.confirm(
        "Reset match personalization? Your match history stays. Simpary will relearn from new searches and activity.",
      )
    ) {
      return;
    }

    try {
      setResetting(true);
      await api.delete("/users/me/shopper-model");
      showToast?.({
        message: "Match personalization reset.",
        type: "success",
      });
    } catch {
      showToast?.({
        message: "Could not reset personalization. Try again.",
        type: "error",
      });
    } finally {
      setResetting(false);
    }
  };

  const handleSaveShoppingStyle = async () => {
    try {
      setSavingStyle(true);
      const { data: updated } = await api.patch("/users/me", {
        profile: {
          showShoppingStyle: Boolean(showShoppingStyle),
          shoppingStyleLabel: shoppingStyleLabel.trim(),
        },
      });
      setUser(updated);
      showToast?.({
        message: "Shopping style visibility updated.",
        type: "success",
      });
    } catch {
      showToast?.({
        message: "Could not update shopping style settings.",
        type: "error",
      });
    } finally {
      setSavingStyle(false);
    }
  };

  return (
    <div>
      <p style={{ fontFamily: "var(--font-family)", marginBottom: "1rem" }}>
        Simpary uses your AI chats on Home and product activity to show match
        scores and recommendations. You do not need to manage this — it updates
        automatically.
      </p>
      <p style={{ fontFamily: "var(--font-family)", marginBottom: "1rem" }}>
        To set budget and priorities yourself, use{" "}
        <strong>Shopping preferences</strong> in the sections above.
      </p>
      <p style={{ fontFamily: "var(--font-family)", marginBottom: "1rem" }}>
        <Link to="../matches">View your match history</Link>
        {" · "}
        <PublicPageLink pageKey="feed" to="/community?tab=matches">
          Open My matches on Home
        </PublicPageLink>
      </p>

      <div
        style={{
          fontFamily: "var(--font-family)",
          marginBottom: "1.25rem",
          paddingTop: "0.5rem",
          borderTop: "1px solid var(--color-border, #e5e7eb)",
        }}
      >
        <h3
          style={{
            fontFamily: "var(--font-family)",
            fontSize: "1rem",
            marginBottom: "0.5rem",
          }}
        >
          Public shopping style
        </h3>
        <p style={{ fontFamily: "var(--font-family)", marginBottom: "0.75rem" }}>
          Optionally show a short, non-technical label on your public profile
          (for example categories you shop for). Never shows raw chat content.
        </p>
        <label
          style={{
            fontFamily: "var(--font-family)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.75rem",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showShoppingStyle}
            onChange={(e) => setShowShoppingStyle(e.target.checked)}
          />
          Show shopping style on my public profile
        </label>
        <label
          style={{
            fontFamily: "var(--font-family)",
            display: "block",
            marginBottom: "0.35rem",
          }}
        >
          Custom label (optional, max 120 characters)
        </label>
        <input
          type="text"
          maxLength={120}
          value={shoppingStyleLabel}
          onChange={(e) => setShoppingStyleLabel(e.target.value)}
          placeholder="Leave blank to auto-generate from your activity"
          style={{
            fontFamily: "var(--font-family)",
            width: "100%",
            maxWidth: "420px",
            marginBottom: "0.75rem",
          }}
        />
        <button
          type="button"
          className="buttonSecondary"
          onClick={handleSaveShoppingStyle}
          disabled={savingStyle}
        >
          {savingStyle ? "Saving…" : "Save shopping style"}
        </button>
      </div>

      <button
        type="button"
        className="buttonSecondary"
        onClick={handleReset}
        disabled={resetting}
      >
        {resetting ? "Resetting…" : "Reset match personalization"}
      </button>
    </div>
  );
};

export default MatchPersonalizationSection;
